import { lazy } from "react";

import { RoutePaths } from "./enums";
import { IRoute } from "./types";

const lazyLoadComponent = (importRes: any) => async () => {
  const Component = await importRes.catch((err: any) => {
    // If it's a chunk load error (most common)
    if (
      err?.message?.includes("Failed to fetch dynamically imported module") ||
      err?.message?.includes("Loading chunk") ||
      err?.message?.includes("ChunkLoadError")
    ) {
      console.error("Chunk load failed, forcing page reload...", err);
      if ("serviceWorker" in navigator) {
        navigator.serviceWorker
          .getRegistrations()
          .then(function (registrations) {
            for (const registration of registrations) {
              // eslint-disable-next-line spellcheck/spell-checker
              registration.unregister();
            }
          })
          .then(() => window.location.reload());
      }
    }

    // eslint-disable-next-line spellcheck/spell-checker
    throw err; // rethrow so Suspense fallback still works
  });

  return { default: Component.default };
};

export const routes: IRoute[] = [
  {
    path: RoutePaths.HOME,
    element: lazy(lazyLoadComponent(import("./homepage"))),
    protected: true,
    children: [
      {
        path: `${RoutePaths.HOMEPAGE}/:id` as RoutePaths,
        protected: false,
        element: lazy(
          lazyLoadComponent(
            import("pages/home/components/homepage-modal/component")
          )
        ),
      },
    ],
  },
  {
    path: RoutePaths.SIGN_IN,
    element: lazy(lazyLoadComponent(import("pages/sign-in"))),
    protected: false,
  },
  {
    path: RoutePaths.USER_MANAGEMENT,
    element: lazy(lazyLoadComponent(import("pages/user-management"))),
    protected: true,
    children: [
      {
        path: `${RoutePaths.USER_MANAGEMENT}/:id` as RoutePaths,
        protected: true,
      },
    ],
  },
  {
    path: RoutePaths.PERMISSION_MANAGEMENT,
    element: lazy(lazyLoadComponent(import("pages/permission-management"))),
    protected: true,
  },
  {
    path: RoutePaths.ANNOUNCEMENTS,
    element: lazy(
      lazyLoadComponent(import("pages/user-interaction/announcements"))
    ),
    protected: true,
    children: [
      {
        path: `${RoutePaths.ANNOUNCEMENTS}/:id` as RoutePaths,
        protected: true,
      },
    ],
  },
  {
    path: RoutePaths.UPCOMING_EVENTS,
    element: lazy(
      lazyLoadComponent(import("pages/user-interaction/upcoming-events"))
    ),
    protected: true,
    children: [
      {
        path: `${RoutePaths.UPCOMING_EVENTS}/:id` as RoutePaths,
        protected: true,
      },
    ],
  },
  {
    path: RoutePaths.WEEKLY_UPDATES,
    element: lazy(
      lazyLoadComponent(import("pages/user-interaction/weekly-updates"))
    ),
    protected: true,
    children: [
      {
        path: `${RoutePaths.WEEKLY_UPDATES}/:id` as RoutePaths,
        protected: true,
      },
    ],
  },
  {
    path: RoutePaths.STATEMENTS,
    element: lazy(
      lazyLoadComponent(import("pages/user-interaction/statements"))
    ),
    protected: true,
    children: [
      {
        path: `${RoutePaths.STATEMENTS}/:id` as RoutePaths,
        protected: true,
      },
    ],
  },
  {
    path: RoutePaths.REPORTS,
    element: lazy(lazyLoadComponent(import("pages/user-interaction/reports"))),
    protected: true,
    children: [
      {
        path: `${RoutePaths.REPORTS}/:id` as RoutePaths,
        protected: true,
      },
    ],
  },
  {
    path: RoutePaths.STATEMENT_TYPE,
    element: lazy(
      lazyLoadComponent(import("pages/user-interaction/statement-type"))
    ),
    protected: true,
    children: [
      {
        path: `${RoutePaths.STATEMENT_TYPE}/:id` as RoutePaths,
        protected: true,
      },
    ],
  },
  {
    path: RoutePaths.REPORT_TYPE,
    element: lazy(
      lazyLoadComponent(import("pages/user-interaction/report-type"))
    ),
    protected: true,
    children: [
      {
        path: `${RoutePaths.REPORT_TYPE}/:id` as RoutePaths,
        protected: true,
      },
    ],
  },
  {
    path: RoutePaths.EXPECTED_CONVERSION,
    element: lazy(
      lazyLoadComponent(import("pages/library/expected-conversion"))
    ),
    protected: true,
    children: [
      {
        path: `${RoutePaths.EXPECTED_CONVERSION}/:id` as RoutePaths,
        protected: true,
      },
    ],
  },
  {
    path: RoutePaths.CONVERSION_GRADING,
    element: lazy(
      lazyLoadComponent(import("pages/library/conversion-grading"))
    ),
    protected: true,
    children: [
      {
        path: `${RoutePaths.CONVERSION_GRADING}/:id` as RoutePaths,
        protected: true,
      },
    ],
  },
  {
    path: RoutePaths.NOTIFICATION_SETTINGS,
    element: lazy(
      lazyLoadComponent(import("pages/administration/notification-settings"))
    ),
    protected: true,
    children: [
      {
        path: `${RoutePaths.NOTIFICATION_SETTINGS}/:id` as RoutePaths,
        protected: true,
      },
    ],
  },
  {
    path: RoutePaths.HR_EMPLOYEE,
    element: lazy(lazyLoadComponent(import("pages/hr/employee"))),
    protected: true,
    children: [
      {
        path: `${RoutePaths.HR_EMPLOYEE}/:id` as RoutePaths,
        protected: true,
      },
    ],
  },
  {
    path: RoutePaths.HR_EMPLOYEE_COMPENSATION,
    element: lazy(lazyLoadComponent(import("pages/hr/employee-compensation"))),
    protected: true,
    children: [
      {
        path: `${RoutePaths.HR_EMPLOYEE_COMPENSATION}/:id` as RoutePaths,
        protected: true,
      },
    ],
  },
  {
    path: RoutePaths.HR_FINANCIAL_REPORT,
    element: lazy(lazyLoadComponent(import("pages/hr/financial-report"))),
    protected: true,
  },
  {
    path: RoutePaths.HR_POINT_SYSTEM_QUANT_FUND_TIER,
    element: lazy(lazyLoadComponent(import("pages/hr/quant-fund-tier"))),
    protected: true,
  },
  {
    path: RoutePaths.HR_POINT_SYSTEM_QUANT_POINTS,
    element: lazy(lazyLoadComponent(import("pages/hr/quant-points"))),
    protected: true,
  },
  {
    path: RoutePaths.HR_POINT_SYSTEM_QUANT_PERFORMANCE,
    element: lazy(lazyLoadComponent(import("pages/hr/quant-performance"))),
    protected: true,
  },
  {
    path: RoutePaths.HR_POINT_SYSTEM_QUANT_DISTRIBUTION,
    element: lazy(lazyLoadComponent(import("pages/hr/quant-distribution"))),
    protected: true,
    children: [
      {
        path: `${RoutePaths.HR_POINT_SYSTEM_QUANT_DISTRIBUTION}/:id` as RoutePaths,
        protected: true,
      },
    ],
  },
  {
    path: RoutePaths.HR_POINT_SYSTEM_VENTURE_POINTS,
    element: lazy(lazyLoadComponent(import("pages/hr/venture-points"))),
    protected: true,
    children: [
      {
        path: `${RoutePaths.HR_POINT_SYSTEM_VENTURE_POINTS}/:id` as RoutePaths,
        protected: true,
      },
    ],
  },
  {
    path: RoutePaths.HR_POINT_SYSTEM_VENTURE_PERFORMANCE,
    element: lazy(lazyLoadComponent(import("pages/hr/venture-performance"))),
    protected: true,
    children: [
      {
        path: `${RoutePaths.HR_POINT_SYSTEM_VENTURE_PERFORMANCE}/:id` as RoutePaths,
        protected: true,
      },
    ],
  },
  {
    path: RoutePaths.HR_POINT_SYSTEM_VENTURE_DISTRIBUTION,
    element: lazy(lazyLoadComponent(import("pages/hr/venture-distribution"))),
    protected: true,
    children: [
      {
        path: `${RoutePaths.HR_POINT_SYSTEM_VENTURE_DISTRIBUTION}/:id` as RoutePaths,
        protected: true,
      },
    ],
  },
  {
    path: RoutePaths.HR_POINT_SYSTEM_CAPITAL_FORMATION_TIER,
    element: lazy(lazyLoadComponent(import("pages/hr/capital-formation-tier"))),
    protected: true,
    children: [
      {
        path: `${RoutePaths.HR_POINT_SYSTEM_CAPITAL_FORMATION_TIER}/:id` as RoutePaths,
        protected: true,
      },
    ],
  },
  {
    path: RoutePaths.HR_POINT_SYSTEM_CAPITAL_FORMATION_DISTRIBUTION,
    element: lazy(
      lazyLoadComponent(import("pages/hr/capital-formation-distribution"))
    ),
    protected: true,
    children: [
      {
        path: `${RoutePaths.HR_POINT_SYSTEM_CAPITAL_FORMATION_DISTRIBUTION}/:id` as RoutePaths,
        protected: true,
      },
    ],
  },
  {
    path: RoutePaths.HR_EMPLOYEE_LEAVE,
    element: lazy(lazyLoadComponent(import("pages/hr/employee-leave"))),
    protected: true,
    children: [
      {
        path: `${RoutePaths.HR_EMPLOYEE_LEAVE}/:id` as RoutePaths,
        protected: true,
      },
    ],
  },
  {
    path: RoutePaths.HR_HOLIDAY_CALENDAR,
    element: lazy(lazyLoadComponent(import("pages/hr/holiday-calendar"))),
    protected: true,
    children: [
      {
        path: `${RoutePaths.HR_HOLIDAY_CALENDAR}/:id` as RoutePaths,
        protected: true,
      },
    ],
  },
  {
    path: RoutePaths.HR_LIBRARY_DOCUMENT_LIST,
    element: lazy(lazyLoadComponent(import("pages/hr/library/document-list"))),
    protected: true,
    children: [
      {
        path: `${RoutePaths.HR_LIBRARY_DOCUMENT_LIST}/:id` as RoutePaths,
        protected: true,
      },
    ],
  },
  {
    path: RoutePaths.INFRASTRUCTURE_DASHBOARD,
    element: lazy(lazyLoadComponent(import("pages/infrastructure/dashboard"))),
    protected: true,
  },
  {
    path: RoutePaths.QUANT_ALERTS,
    element: lazy(lazyLoadComponent(import("pages/quant/alerts"))),
    protected: true,
  },
  {
    path: RoutePaths.QUANT_CONSUMERS,
    element: lazy(lazyLoadComponent(import("pages/quant/consumers"))),
    protected: true,
  },
  {
    path: RoutePaths.QUANT_ACCOUNTS,
    element: lazy(lazyLoadComponent(import("pages/quant/accounts"))),
    protected: true,
  },
  {
    path: RoutePaths.QUANT_DASHBOARD,
    element: lazy(lazyLoadComponent(import("pages/quant/dashboard"))),
    protected: true,
  },
  {
    path: RoutePaths.INFRASTRUCTURE_SYSTEM_TASKS,
    element: lazy(
      lazyLoadComponent(import("pages/infrastructure/system-tasks"))
    ),
    protected: true,
  },
  {
    path: RoutePaths.INFRASTRUCTURE_ACCOUNTS,
    element: lazy(lazyLoadComponent(import("pages/infrastructure/accounts"))),
    protected: true,
    children: [
      {
        path: `${RoutePaths.INFRASTRUCTURE_ACCOUNTS}/:id` as RoutePaths,
        protected: true,
      },
    ],
  },
  {
    path: RoutePaths.CAPITAL_FORMATION_DASHBOARD,
    element: lazy(
      lazyLoadComponent(import("pages/capital-formation/dashboard"))
    ),
    protected: true,
  },
  {
    path: RoutePaths.CAPITAL_FORMATION_LEAD_MAPPING,
    element: lazy(
      lazyLoadComponent(import("pages/capital-formation/lead-mapping"))
    ),
    protected: true,
  },
  {
    path: RoutePaths.CAPITAL_FORMATION_LEAD_REPORTING,
    element: lazy(
      lazyLoadComponent(import("pages/capital-formation/lead-reporting"))
    ),
    protected: true,
  },
  {
    path: RoutePaths.CAPITAL_FORMATION_KPI_PERFORMANCE,
    element: lazy(
      lazyLoadComponent(import("pages/capital-formation/kpi-performance"))
    ),
    protected: true,
  },
  {
    path: RoutePaths.CAPITAL_FORMATION_CI_RESOURCES,
    element: lazy(
      lazyLoadComponent(import("pages/capital-formation/resources"))
    ),
    protected: true,
    children: [
      {
        path: `${RoutePaths.CAPITAL_FORMATION_CI_RESOURCES}/:id` as RoutePaths,
        protected: true,
      },
    ],
  },
  {
    path: RoutePaths.CAPITAL_FORMATION_SNAPSHOTS,
    element: lazy(
      lazyLoadComponent(import("pages/capital-formation/snapshots"))
    ),
    protected: true,
  },
  {
    path: RoutePaths.CAPITAL_FORMATION_FUND_RAISING,
    element: lazy(
      lazyLoadComponent(import("pages/capital-formation/fund-raising"))
    ),
    protected: true,
  },
  {
    path: RoutePaths.CAPITAL_FORMATION_CRST_INVESTOR_DATABASE,
    element: lazy(
      lazyLoadComponent(
        import("pages/capital-formation/crst-investor-database")
      )
    ),
    protected: true,
    children: [
      {
        path: `${RoutePaths.CAPITAL_FORMATION_CRST_INVESTOR_DATABASE}/:id` as RoutePaths,
        protected: true,
      },
    ],
  },
  {
    path: RoutePaths.CAPITAL_INTRODUCER_PROFILES,
    element: lazy(lazyLoadComponent(import("pages/link-agent/profiles"))),
    protected: true,
    children: [
      {
        path: `${RoutePaths.CAPITAL_INTRODUCER_PROFILES}/:id` as RoutePaths,
        protected: true,
      },
    ],
  },
  {
    path: RoutePaths.CAPITAL_INTRODUCER_PAYOUTS,
    element: lazy(lazyLoadComponent(import("pages/link-agent/payouts"))),
    protected: true,
    children: [
      {
        path: `${RoutePaths.CAPITAL_INTRODUCER_PAYOUTS}/:linkAgentProfileId` as RoutePaths,
        protected: false,
        element: lazy(
          lazyLoadComponent(
            import(
              "pages/link-agent/payouts/components/payout-entity/component"
            )
          )
        ),
        children: [
          {
            path: `${RoutePaths.CAPITAL_INTRODUCER_PAYOUTS}/:linkAgentProfileId/:id` as RoutePaths,
            protected: false,
          },
        ],
      },
    ],
  },
  {
    path: RoutePaths.CAPITAL_INTRODUCER_REPORTS,
    element: lazy(lazyLoadComponent(import("pages/link-agent/reports"))),
    protected: true,
    children: [
      {
        path: `${RoutePaths.CAPITAL_INTRODUCER_REPORTS}/:id` as RoutePaths,
        protected: true,
        element: lazy(
          lazyLoadComponent(
            import(
              "pages/link-agent/reports/components/capital-introducer-report-entity/component"
            )
          )
        ),
      },
    ],
  },
  {
    path: RoutePaths.CAPITAL_INTRODUCER_KPI_REPORT,
    element: lazy(
      lazyLoadComponent(import("components/link-agent-kpi-report"))
    ),
    protected: true,
  },
  {
    path: RoutePaths.INVESTMENT_DATABASE,
    element: lazy(lazyLoadComponent(import("pages/investment/profiles"))),
    protected: true,
    children: [
      {
        path: `${RoutePaths.INVESTMENT_DATABASE}/:id` as RoutePaths,
        protected: true,
      },
    ],
  },
  {
    path: RoutePaths.INVESTMENT_LIBRARY_DOCUMENT,
    element: lazy(
      lazyLoadComponent(import("pages/investment/library/document"))
    ),
    protected: true,
    children: [
      {
        path: `${RoutePaths.INVESTMENT_LIBRARY_DOCUMENT}/:id` as RoutePaths,
        protected: true,
      },
    ],
  },
  {
    path: RoutePaths.INVESTMENT_LIBRARY_PRODUCT_FUND,
    element: lazy(
      lazyLoadComponent(import("pages/investment/library/product-fund"))
    ),
    protected: true,
    children: [
      {
        path: `${RoutePaths.INVESTMENT_LIBRARY_PRODUCT_FUND}/:id` as RoutePaths,
        protected: true,
      },
    ],
  },
  {
    path: RoutePaths.INVESTMENT_LIBRARY_TAG,
    element: lazy(lazyLoadComponent(import("pages/investment/library/tag"))),
    protected: true,
    children: [
      {
        path: `${RoutePaths.INVESTMENT_LIBRARY_TAG}/:id` as RoutePaths,
        protected: true,
      },
    ],
  },
  {
    path: RoutePaths.INVESTMENT_LIBRARY_NOTE_TYPE,
    element: lazy(
      lazyLoadComponent(import("pages/investment/library/note-type"))
    ),
    protected: true,
    children: [
      {
        path: `${RoutePaths.INVESTMENT_LIBRARY_NOTE_TYPE}/:id` as RoutePaths,
        protected: true,
      },
    ],
  },
  {
    path: RoutePaths.INVESTMENT_LIBRARY_SOURCE_EXCHANGE,
    element: lazy(
      lazyLoadComponent(import("pages/investment/library/source-exchange"))
    ),
    protected: true,
    children: [
      {
        path: `${RoutePaths.INVESTMENT_LIBRARY_SOURCE_EXCHANGE}/:id` as RoutePaths,
        protected: true,
      },
    ],
  },
  {
    path: RoutePaths.FINANCE_DISTRIBUTION_MODELING,
    element: lazy(
      lazyLoadComponent(import("pages/finance/distribution-modeling"))
    ),
    protected: true,
  },
  {
    path: RoutePaths.FINANCE_ASSETS_FIREBLOCKS,
    element: lazy(lazyLoadComponent(import("pages/assets/fireblocks"))),
    protected: true,
  },
  {
    path: RoutePaths.FINANCE_ASSETS_EXCHANGE,
    element: lazy(lazyLoadComponent(import("pages/assets/exchange"))),
    protected: true,
  },
  {
    path: RoutePaths.FINANCE_ASSETS_EXCHANGE_ACCOUNTS,
    element: lazy(lazyLoadComponent(import("pages/assets/exchange-accounts"))),
    protected: true,
    children: [
      {
        path: `${RoutePaths.FINANCE_ASSETS_EXCHANGE_ACCOUNTS}/:id` as RoutePaths,
        protected: true,
      },
    ],
  },
  {
    path: RoutePaths.FINANCE_ASSETS_EXCHANGE_TYPE,
    element: lazy(lazyLoadComponent(import("pages/assets/exchange-type"))),
    protected: true,
    children: [
      {
        path: `${RoutePaths.FINANCE_ASSETS_EXCHANGE_TYPE}/:id` as RoutePaths,
        protected: true,
      },
    ],
  },
  {
    path: RoutePaths.FINANCE_DASHBOARD,
    element: lazy(lazyLoadComponent(import("pages/finance/dashboard"))),
    protected: true,
  },
  {
    path: RoutePaths.FINANCE_ASSETS_LEGAL_ENTITIES,
    element: lazy(lazyLoadComponent(import("pages/finance/legal-entities"))),
    protected: true,
    children: [
      {
        path: `${RoutePaths.FINANCE_ASSETS_LEGAL_ENTITIES}/:id` as RoutePaths,
        protected: true,
      },
    ],
  },
  {
    path: RoutePaths.FINANCE_ASSETS_BANK_ACCOUNTS,
    element: lazy(lazyLoadComponent(import("pages/finance/bank-accounts"))),
    protected: true,
    children: [
      {
        path: `${RoutePaths.FINANCE_ASSETS_BANK_ACCOUNTS}/:id` as RoutePaths,
        protected: true,
      },
    ],
  },
  {
    path: RoutePaths.FINANCE_ASSETS_SNAPSHOTS,
    element: lazy(lazyLoadComponent(import("pages/assets/snapshots"))),
    protected: true,
  },
  {
    path: RoutePaths.FINANCE_CAPITAL_MOVEMENT,
    element: lazy(lazyLoadComponent(import("pages/finance/capital-movement"))),
    protected: true,
    children: [
      {
        path: `${RoutePaths.FINANCE_CAPITAL_MOVEMENT}/:id` as RoutePaths,
        protected: true,
      },
    ],
  },
  {
    path: RoutePaths.FINANCE_CAPITAL_MOVEMENT_DIVIDEND,
    element: lazy(
      lazyLoadComponent(import("pages/finance/capital-movement-dividend"))
    ),
    protected: true,
    children: [
      {
        path: `${RoutePaths.FINANCE_CAPITAL_MOVEMENT_DIVIDEND}/:id` as RoutePaths,
        protected: true,
      },
    ],
  },
  {
    path: RoutePaths.FINANCE_INTRA_GROUP_LOANS,
    element: lazy(lazyLoadComponent(import("pages/finance/intra-group-loans"))),
    protected: true,
    children: [
      {
        path: `${RoutePaths.FINANCE_INTRA_GROUP_LOANS}/:id` as RoutePaths,
        protected: true,
      },
    ],
  },
  {
    path: RoutePaths.PRODUCT_FUND_SERIES,
    element: lazy(lazyLoadComponent(import("pages/product-fund/series"))),
    protected: true,
  },
  {
    path: RoutePaths.REPORTING,
    element: lazy(lazyLoadComponent(import("pages/investment/reporting"))),
    protected: true,
    children: [
      {
        path: `${RoutePaths.REPORTING}/:id` as RoutePaths,
        protected: true,
      },
    ],
  },
  {
    path: RoutePaths.VENTURE_DASHBOARD,
    element: lazy(lazyLoadComponent(import("pages/venture/dashboard"))),
    protected: true,
  },
  {
    path: RoutePaths.VENTURE_PROJECT_PROFILE,
    element: lazy(lazyLoadComponent(import("pages/venture/project-profile"))),
    protected: true,
    children: [
      {
        path: `${RoutePaths.VENTURE_PROJECT_PROFILE}/:id` as RoutePaths,
        protected: true,
      },
    ],
  },
  {
    path: RoutePaths.VENTURE_PORTFOLIO_CONTROL,
    element: lazy(lazyLoadComponent(import("pages/venture/portfolio-control"))),
    protected: true,
  },
  {
    path: RoutePaths.VENTURE_VALUATION_CONTROL,
    element: lazy(lazyLoadComponent(import("pages/venture/valuation-control"))),
    protected: true,
    children: [
      {
        path: `${RoutePaths.VENTURE_VALUATION_CONTROL}/:id` as RoutePaths,
        protected: true,
      },
    ],
  },
  {
    path: RoutePaths.VENTURE_ASSET_ATTRIBUTION,
    element: lazy(lazyLoadComponent(import("pages/venture/asset-attribution"))),
    protected: true,
    children: [
      {
        path: `${RoutePaths.VENTURE_ASSET_ATTRIBUTION}/:id` as RoutePaths,
        protected: true,
      },
    ],
  },
  {
    path: RoutePaths.VENTURE_TGE_CALENDAR,
    element: lazy(lazyLoadComponent(import("pages/venture/tge-calendar"))),
    protected: true,
  },
  {
    path: RoutePaths.VENTURE_MODELING_TOKEN_MODELING,
    element: lazy(
      lazyLoadComponent(import("pages/venture/modeling/token-modeling"))
    ),
    protected: true,
  },
  {
    path: RoutePaths.VENTURE_MODELING_VESTING_MODELING,
    element: lazy(
      lazyLoadComponent(import("pages/venture/modeling/vesting-modeling"))
    ),
    protected: true,
  },
  {
    path: RoutePaths.VENTURE_KPI,
    element: lazy(lazyLoadComponent(import("pages/venture/kpi"))),
    protected: true,
  },
  {
    path: RoutePaths.VENTURE_SNAPSHOTS,
    element: lazy(lazyLoadComponent(import("pages/venture/snapshots"))),
    protected: true,
  },
  {
    path: RoutePaths.VENTURE_CONTACT,
    element: lazy(lazyLoadComponent(import("pages/venture/contact"))),
    protected: true,
    children: [
      {
        path: `${RoutePaths.VENTURE_CONTACT}/:id` as RoutePaths,
        protected: true,
      },
    ],
  },
  {
    path: RoutePaths.VENTURE_LIBRARY_CONTACT_TYPE,
    element: lazy(
      lazyLoadComponent(import("pages/venture/library/contact-type"))
    ),
    protected: true,
    children: [
      {
        path: `${RoutePaths.VENTURE_LIBRARY_CONTACT_TYPE}/:id` as RoutePaths,
        protected: true,
      },
    ],
  },
  {
    path: RoutePaths.VENTURE_LIBRARY_INVESTMENT_METHOD,
    element: lazy(
      lazyLoadComponent(import("pages/venture/library/investment-method"))
    ),
    protected: true,
    children: [
      {
        path: `${RoutePaths.VENTURE_LIBRARY_INVESTMENT_METHOD}/:id` as RoutePaths,
        protected: true,
      },
    ],
  },
  {
    path: RoutePaths.VENTURE_LIBRARY_BLOCKCHAIN_LIST,
    element: lazy(
      lazyLoadComponent(import("pages/venture/library/blockchain-list"))
    ),
    protected: true,
    children: [
      {
        path: `${RoutePaths.VENTURE_LIBRARY_BLOCKCHAIN_LIST}/:id` as RoutePaths,
        protected: true,
      },
    ],
  },
  {
    path: RoutePaths.VENTURE_LIBRARY_ORIGINATION_SOURCE,
    element: lazy(
      lazyLoadComponent(import("pages/venture/library/origination-source"))
    ),
    protected: true,
    children: [
      {
        path: `${RoutePaths.VENTURE_LIBRARY_ORIGINATION_SOURCE}/:id` as RoutePaths,
        protected: true,
      },
    ],
  },
  {
    path: RoutePaths.VENTURE_LIBRARY_SECTOR,
    element: lazy(lazyLoadComponent(import("pages/venture/library/sector"))),
    protected: true,
    children: [
      {
        path: `${RoutePaths.VENTURE_LIBRARY_SECTOR}/:id` as RoutePaths,
        protected: true,
      },
    ],
  },
  {
    path: RoutePaths.VENTURE_LIBRARY_TAGS,
    element: lazy(lazyLoadComponent(import("pages/venture/library/tags"))),
    protected: true,
    children: [
      {
        path: `${RoutePaths.VENTURE_LIBRARY_TAGS}/:id` as RoutePaths,
        protected: true,
      },
    ],
  },
  {
    path: RoutePaths.VENTURE_LIBRARY_NOTE_TYPE,
    element: lazy(lazyLoadComponent(import("pages/venture/library/note-type"))),
    protected: true,
    children: [
      {
        path: `${RoutePaths.VENTURE_LIBRARY_NOTE_TYPE}/:id` as RoutePaths,
        protected: true,
      },
    ],
  },
  {
    path: RoutePaths.VENTURE_LIBRARY_DOCUMENT_LIST,
    element: lazy(
      lazyLoadComponent(import("pages/venture/library/document-list"))
    ),
    protected: true,
    children: [
      {
        path: `${RoutePaths.VENTURE_LIBRARY_DOCUMENT_LIST}/:id` as RoutePaths,
        protected: true,
      },
    ],
  },
  {
    path: RoutePaths.WESTEROS_CLIENT_DATABASE,
    element: lazy(lazyLoadComponent(import("pages/westeros/clients"))),
    protected: true,
    children: [
      {
        path: `${RoutePaths.WESTEROS_CLIENT_DATABASE}/:id` as RoutePaths,
        protected: true,
      },
    ],
  },
  {
    path: RoutePaths.WESTEROS_LIBRARY_SERVICE,
    element: lazy(lazyLoadComponent(import("pages/westeros/library/service"))),
    protected: true,
    children: [
      {
        path: `${RoutePaths.WESTEROS_LIBRARY_SERVICE}/:id` as RoutePaths,
        protected: true,
      },
    ],
  },
  {
    path: RoutePaths.WESTEROS_LIBRARY_NOTE_TYPE,
    element: lazy(
      lazyLoadComponent(import("pages/westeros/library/note-type"))
    ),
    protected: true,
    children: [
      {
        path: `${RoutePaths.WESTEROS_LIBRARY_NOTE_TYPE}/:id` as RoutePaths,
        protected: true,
      },
    ],
  },
  {
    path: RoutePaths.WESTEROS_LIBRARY_DOCUMENT_LIST,
    element: lazy(
      lazyLoadComponent(import("pages/westeros/library/document-list"))
    ),
    protected: true,
    children: [
      {
        path: `${RoutePaths.WESTEROS_LIBRARY_DOCUMENT_LIST}/:id` as RoutePaths,
        protected: true,
      },
    ],
  },
  {
    path: RoutePaths.WESTEROS_LIBRARY_TAGS,
    element: lazy(lazyLoadComponent(import("pages/westeros/library/tags"))),
    protected: true,
    children: [
      {
        path: `${RoutePaths.WESTEROS_LIBRARY_TAGS}/:id` as RoutePaths,
        protected: true,
      },
    ],
  },
  {
    path: RoutePaths.WESTEROS_PWM_PROFILE,
    element: lazy(lazyLoadComponent(import("pages/pwm/profile"))),
    protected: true,
    children: [
      {
        path: `${RoutePaths.WESTEROS_PWM_PROFILE}/:id` as RoutePaths,
        protected: true,
      },
    ],
  },
  {
    path: RoutePaths.WESTEROS_PWM_TRADING,
    element: lazy(lazyLoadComponent(import("pages/pwm/trading"))),
    protected: true,
    children: [
      {
        path: `${RoutePaths.WESTEROS_PWM_TRADING}/:id` as RoutePaths,
        protected: true,
      },
    ],
  },
  {
    path: RoutePaths.WESTEROS_PWM_EXCHANGE,
    element: lazy(lazyLoadComponent(import("pages/pwm/exchange"))),
    protected: true,
    children: [
      {
        path: `${RoutePaths.WESTEROS_PWM_EXCHANGE}/:id` as RoutePaths,
        protected: true,
      },
    ],
  },
  {
    path: RoutePaths.WESTEROS_PWM_STRATEGY,
    element: lazy(lazyLoadComponent(import("pages/pwm/strategy"))),
    protected: true,
    children: [
      {
        path: `${RoutePaths.WESTEROS_PWM_STRATEGY}/:id` as RoutePaths,
        protected: true,
      },
    ],
  },
  {
    path: RoutePaths.WESTEROS_PWM_PROFILE_REPORT,
    element: lazy(lazyLoadComponent(import("pages/pwm/profile-report"))),
    protected: true,
  },
  {
    path: RoutePaths.WESTEROS_PWM_LIBRARY_DOCUMENT_LIST,
    element: lazy(lazyLoadComponent(import("pages/pwm/library/document-list"))),
    protected: true,
    children: [
      {
        path: `${RoutePaths.WESTEROS_PWM_LIBRARY_DOCUMENT_LIST}/:id` as RoutePaths,
        protected: true,
      },
    ],
  },
  {
    path: RoutePaths.WESTEROS_PWM_LIBRARY_NOTE_TYPE,
    element: lazy(lazyLoadComponent(import("pages/pwm/library/note-type"))),
    protected: true,
    children: [
      {
        path: `${RoutePaths.WESTEROS_PWM_LIBRARY_NOTE_TYPE}/:id` as RoutePaths,
        protected: true,
      },
    ],
  },
  {
    path: RoutePaths.WESTEROS_PWM_LIBRARY_EXCHANGE_ITEM,
    element: lazy(lazyLoadComponent(import("pages/pwm/library/exchange-item"))),
    protected: true,
    children: [
      {
        path: `${RoutePaths.WESTEROS_PWM_LIBRARY_EXCHANGE_ITEM}/:id` as RoutePaths,
        protected: true,
      },
    ],
  },
  {
    path: RoutePaths.WESTEROS_PWM_LIBRARY_STRATEGY_ITEM,
    element: lazy(lazyLoadComponent(import("pages/pwm/library/strategy-item"))),
    protected: true,
    children: [
      {
        path: `${RoutePaths.WESTEROS_PWM_LIBRARY_STRATEGY_ITEM}/:id` as RoutePaths,
        protected: true,
      },
    ],
  },
  {
    path: RoutePaths.ADVISORY_PROJECT,
    element: lazy(lazyLoadComponent(import("pages/advisory/project"))),
    protected: true,
    children: [
      {
        path: `${RoutePaths.ADVISORY_PROJECT}/:id` as RoutePaths,
        protected: true,
      },
    ],
  },
  {
    path: RoutePaths.ADVISORY_BUYER,
    element: lazy(lazyLoadComponent(import("pages/advisory/buyer"))),
    protected: true,
    children: [
      {
        path: `${RoutePaths.ADVISORY_BUYER}/:id` as RoutePaths,
        protected: true,
      },
    ],
  },
  {
    path: RoutePaths.ADVISORY_PURCHASE,
    element: lazy(lazyLoadComponent(import("pages/advisory/purchase"))),
    protected: true,
    children: [
      {
        path: `${RoutePaths.ADVISORY_PURCHASE}/:id` as RoutePaths,
        protected: true,
      },
    ],
  },
  {
    path: RoutePaths.ADVISORY_TRANCHE,
    element: lazy(lazyLoadComponent(import("pages/advisory/tranche"))),
    protected: true,
    children: [
      {
        path: `${RoutePaths.ADVISORY_TRANCHE}/:id` as RoutePaths,
        protected: true,
      },
    ],
  },
  {
    path: RoutePaths.ADVISORY_EXIT_PLAN,
    element: lazy(lazyLoadComponent(import("pages/advisory/exit-plan"))),
    protected: true,
    children: [
      {
        path: `${RoutePaths.ADVISORY_EXIT_PLAN}/:id` as RoutePaths,
        protected: true,
      },
    ],
  },
  {
    path: RoutePaths.ADVISORY_DISTRIBUTION,
    element: lazy(lazyLoadComponent(import("pages/advisory/distribution"))),
    protected: true,
    children: [
      {
        path: `${RoutePaths.ADVISORY_DISTRIBUTION}/:id` as RoutePaths,
        protected: true,
      },
    ],
  },
  {
    path: RoutePaths.ADVISORY_REPORT,
    element: lazy(lazyLoadComponent(import("pages/advisory/report"))),
    protected: true,
  },
  {
    path: RoutePaths.ADVISORY_LIBRARY_FIREBLOCKS_WALLET,
    element: lazy(
      lazyLoadComponent(import("pages/advisory/library/fireblocks-wallet"))
    ),
    protected: true,
    children: [
      {
        path: `${RoutePaths.ADVISORY_LIBRARY_FIREBLOCKS_WALLET}/:id` as RoutePaths,
        protected: true,
      },
    ],
  },
  {
    path: RoutePaths.EMAILS,
    element: lazy(lazyLoadComponent(import("pages/email/email-system"))),
    protected: true,
    children: [
      {
        path: RoutePaths.EMAIL_SEND_EMAIL,
        protected: true,
        isAllowedStrict: true,
        element: lazy(
          lazyLoadComponent(
            import(
              "pages/email/email-system/components/send-email-section/component"
            )
          )
        ),
      },
      {
        path: `${RoutePaths.EMAILS}/:id` as RoutePaths,
        protected: true,
        element: lazy(
          lazyLoadComponent(
            import(
              "pages/email/email-system/components/email-history-modal/component"
            )
          )
        ),
      },
    ],
  },
  {
    path: RoutePaths.EMAIL_TEMPLATES,
    element: lazy(lazyLoadComponent(import("pages/email/email-templates"))),
    protected: true,
    children: [
      {
        path: `${RoutePaths.EMAIL_TEMPLATES}/:id` as RoutePaths,
        protected: true,
      },
    ],
  },
  {
    path: RoutePaths.SYSTEM_TASK_MANAGEMENT_TASK,
    element: lazy(lazyLoadComponent(import("pages/task-management/task"))),
    protected: true,
    children: [
      {
        path: `${RoutePaths.SYSTEM_TASK_MANAGEMENT_TASK}/:id` as RoutePaths,
        protected: true,
      },
    ],
  },
  {
    path: RoutePaths.SYSTEM_TASK_MANAGEMENT_RECURRING_TASK,
    element: lazy(
      lazyLoadComponent(import("pages/task-management/recurring-task"))
    ),
    protected: true,
    children: [
      {
        path: `${RoutePaths.SYSTEM_TASK_MANAGEMENT_RECURRING_TASK}/:id` as RoutePaths,
        protected: true,
      },
    ],
  },
  {
    path: RoutePaths.SYSTEM_TASK_MANAGEMENT_REMIND,
    element: lazy(lazyLoadComponent(import("pages/task-management/remind"))),
    protected: true,
    children: [
      {
        path: `${RoutePaths.SYSTEM_TASK_MANAGEMENT_REMIND}/:id` as RoutePaths,
        protected: true,
      },
    ],
  },
  {
    path: RoutePaths.SYSTEM_TASK_MANAGEMENT_TASK_REPORTING,
    element: lazy(
      lazyLoadComponent(import("pages/task-management/task-reporting"))
    ),
    protected: true,
  },
  {
    path: RoutePaths.SYSTEM_TASK_MANAGEMENT_TASK_TYPE,
    element: lazy(
      lazyLoadComponent(import("pages/task-management/library/task-type"))
    ),
    protected: true,
    children: [
      {
        path: `${RoutePaths.SYSTEM_TASK_MANAGEMENT_TASK_TYPE}/:id` as RoutePaths,
        protected: true,
      },
    ],
  },
  {
    path: RoutePaths.SYSTEM_TASK_MANAGEMENT_NOTE_TYPE,
    element: lazy(
      lazyLoadComponent(import("pages/task-management/library/note-type"))
    ),
    protected: true,
    children: [
      {
        path: `${RoutePaths.SYSTEM_TASK_MANAGEMENT_NOTE_TYPE}/:id` as RoutePaths,
        protected: true,
      },
    ],
  },
  {
    path: RoutePaths.SYSTEM_TASK_MANAGEMENT_REMIND_TYPE,
    element: lazy(
      lazyLoadComponent(import("pages/task-management/library/remind-type"))
    ),
    protected: true,
    children: [
      {
        path: `${RoutePaths.SYSTEM_TASK_MANAGEMENT_REMIND_TYPE}/:id` as RoutePaths,
        protected: true,
      },
    ],
  },
  {
    path: RoutePaths.USER_CAPITAL_INTRODUCER_MY_INFO,
    element: lazy(
      lazyLoadComponent(import("pages/user-portal/link-agent/my-info"))
    ),
    protected: true,
  },
  {
    path: RoutePaths.USER_CAPITAL_INTRODUCER_RESOURCES,
    element: lazy(
      lazyLoadComponent(import("pages/user-portal/link-agent/resources"))
    ),
    protected: true,
    children: [
      {
        path: `${RoutePaths.USER_CAPITAL_INTRODUCER_RESOURCES}/:id` as RoutePaths,
        protected: true,
      },
    ],
  },
  {
    path: RoutePaths.USER_CAPITAL_INTRODUCER_REPORTS,
    element: lazy(
      lazyLoadComponent(import("pages/user-portal/link-agent/reports"))
    ),
    protected: true,
    children: [
      {
        path: `${RoutePaths.USER_CAPITAL_INTRODUCER_REPORTS}/:id` as RoutePaths,
        protected: true,
        element: lazy(
          lazyLoadComponent(
            import(
              "pages/link-agent/reports/components/capital-introducer-report-entity/component"
            )
          )
        ),
      },
    ],
  },
  {
    path: RoutePaths.USER_CAPITAL_INTRODUCER_PAYOUTS,
    element: lazy(
      lazyLoadComponent(import("pages/user-portal/link-agent/payouts"))
    ),
    protected: true,
    children: (idToken) => [
      idToken?.linkAgentTypeId === 1
        ? {
            path: `${RoutePaths.USER_CAPITAL_INTRODUCER_PAYOUTS}/:linkAgentProfileId` as RoutePaths,
            protected: false,
            props: { isLinkAgentPayoutPage: true },
            element: lazy(
              lazyLoadComponent(
                import(
                  "pages/link-agent/payouts/components/payout-entity/component"
                )
              )
            ),
            children: [
              {
                path: `${RoutePaths.USER_CAPITAL_INTRODUCER_PAYOUTS}/:linkAgentProfileId/:id` as RoutePaths,
                protected: false,
              },
            ],
          }
        : {
            path: `${RoutePaths.USER_CAPITAL_INTRODUCER_PAYOUTS}/:id` as RoutePaths,
            protected: false,
          },
    ],
  },
  {
    path: RoutePaths.USER_INVESTOR_MY_INFO,
    element: lazy(
      lazyLoadComponent(import("pages/user-portal/investor/my-info"))
    ),
    protected: true,
  },
  {
    path: RoutePaths.USER_INVESTOR_DASHBOARD,
    element: lazy(
      lazyLoadComponent(import("pages/user-portal/investor/dashboard"))
    ),
    protected: true,
  },
  {
    path: RoutePaths.USER_INVESTOR_CAPITAL_MOVEMENT,
    element: lazy(
      lazyLoadComponent(import("pages/user-portal/investor/capital-movement"))
    ),
    protected: true,
  },
  {
    path: RoutePaths.USER_INVESTOR_REPORTS,
    element: lazy(lazyLoadComponent(import("pages/user-interaction/reports"))),
    protected: true,
    children: [
      {
        path: `${RoutePaths.USER_INVESTOR_REPORTS}/:id` as RoutePaths,
        protected: true,
      },
    ],
  },
  {
    path: RoutePaths.USER_INVESTOR_STATEMENTS,
    element: lazy(
      lazyLoadComponent(import("pages/user-interaction/statements"))
    ),
    protected: true,
    children: [
      {
        path: `${RoutePaths.USER_INVESTOR_STATEMENTS}/:id` as RoutePaths,
        protected: true,
      },
    ],
  },
  {
    path: RoutePaths.USER_EMPLOYEE_DASHBOARD,
    element: lazy(
      lazyLoadComponent(import("pages/user-portal/employee/dashboard"))
    ),
    protected: true,
  },
  {
    path: RoutePaths.AI_AGENT_LEADS,
    element: lazy(lazyLoadComponent(import("pages/ai-agent/leads"))),
    protected: true,
  },
  {
    path: RoutePaths.NOT_FOUND,
    element: lazy(lazyLoadComponent(import("pages/404"))),
    protected: false,
  },
  {
    path: RoutePaths.WILDECARD,
    element: lazy(lazyLoadComponent(import("pages/404"))),
    protected: false,
  },
];
